import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { visitorNav } from "./VisitorDirectory";
import QRScanner from "@/components/QRScanner";
import { Overline } from "@/components/Brand";
import { useEvent } from "@/context/EventContext";
import { toast } from "sonner";
import { CheckCircle2, CalendarClock } from "lucide-react";

export default function VisitorScan() {
  const navigate = useNavigate();
  const [done, setDone] = useState(null);
  const { effectivePhase, checkinAllowed } = useEvent();
  const isLive = effectivePhase === "live_event";

  const handle = async (code) => {
    const token = String(code).split("/").pop().trim();
    try {
      const { data: ex } = await api.get(`/exhibitors/by-qr/${token}`);
      if (!checkinAllowed) {
        // Outside live window, QR opens the stall profile only — no check-in is created.
        toast.success(`Opening ${ex.name}`);
        navigate(`/visitor/exhibitor/${ex.id}`);
        return;
      }
      await api.post("/visitor/checkin", { exhibitor_id: ex.id, source: "qr" });
      setDone(ex);
      toast.success(`Checked in at ${ex.name}`);
    } catch (err) {
      toast.error(apiErr(err) === "Stall not found" ? "Unrecognised QR code" : apiErr(err));
    }
  };

  return (
    <Shell nav={visitorNav}>
      <Overline>Instant Check-In</Overline>
      <h1 className="font-display text-3xl gold-text mt-2">Scan Stall QR</h1>
      <p className="text-slate-400 text-sm mt-1">
        {isLive ? "Scan an exhibitor's QR to open their stall & check in." : "Scan a QR to open an exhibitor's stall & catalogue."}
      </p>

      {!isLive && (
        <div className="glass rounded-2xl p-4 mt-4 flex items-start gap-3" data-testid="visitor-scan-phase-notice">
          <CalendarClock size={18} className="text-[var(--jjs-gold-deep)] mt-0.5 shrink-0" />
          <p className="text-sm text-slate-500">
            On-site check-ins open during the show (22–25 Dec). For now, scanning a stall QR simply opens its profile and catalogue.
          </p>
        </div>
      )}

      {done ? (
        <div className="glass rounded-3xl p-7 mt-8 text-center fade-up" data-testid="scan-success">
          <div className="h-16 w-16 rounded-full btn-gold flex items-center justify-center mx-auto gold-glow">
            <CheckCircle2 size={30} className="text-[#0B0F12]" />
          </div>
          <h2 className="font-display text-2xl text-slate-100 mt-4">Checked in!</h2>
          <p className="text-slate-400 mt-1">{done.name} · {done.hall} {done.booth}</p>
          <div className="flex gap-3 mt-6">
            <button onClick={() => navigate(`/visitor/exhibitor/${done.id}`)} className="flex-1 btn-gold py-3 rounded-xl" data-testid="scan-view-stall">View Stall</button>
            <button onClick={() => setDone(null)} className="flex-1 glass py-3 rounded-xl text-slate-300" data-testid="scan-again">Scan Again</button>
          </div>
        </div>
      ) : (
        <div className="glass rounded-3xl p-5 mt-8">
          <QRScanner onResult={handle} label="stall QR" />
        </div>
      )}
    </Shell>
  );
}
